import type { BridgeConfig } from '../../custom-ui/bridge/types';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

type BridgeConfigContext = MakeBridgeContext<'bridgeConfig'>;

type BridgeConfigKeys = keyof BridgeConfig;

// Extracts the first parameter type of a BridgeConfig callback.
// Falls back to `void` for zero-argument callbacks (e.g. `emitReadyEvent`, `refresh`, `open`)
// or for non-function members (e.g. `history`, `extensionBasePath` from HistoryApiProvider).
type BridgeConfigPayload<K extends BridgeConfigKeys> =
	NonNullable<BridgeConfig[K]> extends (...args: infer Args) => any
		? Args extends [infer First, ...any[]]
			? First
			: void
		: void;

/**
 * Creates a bridge method that delegates to a single `BridgeConfig` callback,
 * passing the payload through directly.
 *
 * Contract:
 *   - Callback present  → `(await callback(payload)) ?? true`
 *   - Callback absent   → `false`
 *
 * The callback is read from `bridgeConfig` at invocation time (not creation time),
 * so updates to the product's bridge prop are always reflected.
 */
const createBridgeConfigDelegate =
	<K extends BridgeConfigKeys>(
		callbackName: K,
	): TBridgeMethod<BridgeConfigContext, BridgeConfigPayload<K>> =>
	async ({ bridgeConfig }, payload) => {
		const callback = bridgeConfig[callbackName] as ((payload: unknown) => unknown) | undefined;
		if (!callback) return false;
		return (await callback(payload)) ?? true;
	};

/**
 * Creates a bridge method that delegates to a single `BridgeConfig` callback,
 * re-wrapping the payload as `{ data: payload }` before forwarding.
 *
 * BAD LEGACY PAYLOAD SHAPE — do not use this for new bridge methods.
 *
 * In the legacy bridge path (postRobot), feature handlers received the raw port-rpc
 * event object `{ data: actualPayload }` and product-side `BridgeConfig` callbacks
 * were written to access `opts.data` to get the actual value. The v2 bridge
 * (`bridge-core-reset`) correctly unwraps `{ data }` before invoking the bridge
 * method — so these delegates must re-wrap to maintain backward compatibility with
 * all existing product implementations (Confluence, Jira, xen-editor-provider, etc.)
 * that were written against the legacy contract.
 *
 * New bridge methods should use `createBridgeConfigDelegate` and accept the payload
 * directly (no `.data` wrapping).
 */
const createBridgeConfigDelegateForLegacyWrappedPayload =
	<K extends BridgeConfigKeys>(
		callbackName: K,
	): TBridgeMethod<BridgeConfigContext, BridgeConfigPayload<K>> =>
	async ({ bridgeConfig }, payload) => {
		const callback = bridgeConfig[callbackName] as ((payload: unknown) => unknown) | undefined;
		if (!callback) return false;
		return (await callback({ data: payload })) ?? true;
	};

/**
 * Canonical list of every `BridgeConfig` key exposed as a bridge feature.
 *
 * Keys excluded from this list (e.g. `spaPush`, `history`, `extensionBasePath`)
 * are used internally by other bridge methods and are not directly dispatched
 * from the app iframe.
 */
const BRIDGE_CONFIG_DELEGATE_KEYS = [
	'changeWindowTitle',
	'close',
	'closeFlag',
	'emitReadyEvent',
	'getFrameDispatch',
	'onClose',
	'onError',
	'open',
	'openModal',
	'reconcile',
	'refresh',
	'showFlag',
	'submit',
] as const satisfies readonly BridgeConfigKeys[];

type DelegateKey = (typeof BRIDGE_CONFIG_DELEGATE_KEYS)[number];

/**
 * Subset of {@link BRIDGE_CONFIG_DELEGATE_KEYS} that use the legacy `{ data: payload }` wrapped
 * shape. See `createBridgeConfigDelegateForLegacyWrappedPayload` for the full explanation.
 *
 * The `satisfies readonly DelegateKey[]` constraint enforces — at compile time — that every entry
 * here is also a delegated key. Adding a key here without first adding it to
 * {@link BRIDGE_CONFIG_DELEGATE_KEYS} is a type error.
 */
export const BRIDGE_CONFIG_LEGACY_WRAPPED_DELEGATE_KEYS = [
	'close',
	'onClose',
	'openModal',
	'submit',
] as const satisfies readonly DelegateKey[];

// Per-key typed map — consumers see the correct payload type for each delegate.
export type BridgeConfigDelegates = {
	[K in DelegateKey]: TBridgeMethod<BridgeConfigContext, BridgeConfigPayload<K>>;
};

// Cast required: Object.fromEntries returns { [k: string]: ... } which TypeScript cannot narrow
// to a keyed mapped type. Sound because the keys and factories are derived from the master array.
export const bridgeConfigDelegates = Object.fromEntries(
	BRIDGE_CONFIG_DELEGATE_KEYS.map((key) => [
		key,
		(BRIDGE_CONFIG_LEGACY_WRAPPED_DELEGATE_KEYS as readonly DelegateKey[]).includes(key)
			? createBridgeConfigDelegateForLegacyWrappedPayload(key)
			: createBridgeConfigDelegate(key),
	]),
) as BridgeConfigDelegates;
